import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { LogoutAction } from '../../Redux/Actions/AuthActions';
import AvatarMenu from '../AvatarMenu';
import { Logout, PersonOutline, ShieldOutlined } from '@mui/icons-material';
import { Divider, ListItemIcon, MenuItem, CircularProgress } from '@mui/material';
import { useTheme } from '../../context/ThemeContext';
import { Sun, Moon, Monitor } from 'lucide-react';

export function getCurrentDateTime(separator = '/') {
    const t = new Date();
    const mm = String(t.getMonth() + 1).padStart(2, '0');
    const dd = String(t.getDate()).padStart(2, '0');
    const yyyy = t.getFullYear();
    let h = t.getHours();
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    const hh = String(h).padStart(2, '0');
    const min = String(t.getMinutes()).padStart(2, '0');
    const sec = String(t.getSeconds()).padStart(2, '0');
    return `${mm}${separator}${dd}${separator}${yyyy}&nbsp;&nbsp;&nbsp;${hh}:${min}:${sec} ${ampm}`;
}

export default function Navbar({ onToggleSidebar }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const profile_state = useSelector((state) => state.userDetails);
    const { mode, isDark, toggleTheme } = useTheme();

    const [current_date_time, setCurrentDateTime] = useState(getCurrentDateTime());
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    useEffect(() => {
        const timer = setInterval(() => setCurrentDateTime(getCurrentDateTime()), 1000);
        return () => clearInterval(timer);
    }, []);

    const handleLogout = (e) => {
        e.preventDefault();
        if (isLoggingOut) return;
        setIsLoggingOut(true);
        dispatch(LogoutAction(navigate));
    };

    const userName = profile_state.userProfile?.name || 'Administrator';
    const userRole = profile_state.userProfile?.roles?.[0]?.name || profile_state.userProfile?.role || 'Super Admin';
    const initials = userName
        ? userName.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
        : 'AD';

    return (
        <nav
            className="flex items-center px-4 sm:px-6 shrink-0 sticky top-0 z-[1030] transition-colors duration-200"
            style={{
                height: 60,
                background: 'var(--color-header, #FFFFFF)',
                borderBottom: '1px solid var(--color-border, #E2E8F0)',
            }}
        >
            {/* Left: Sidebar toggle + Role Badge */}
            <div className="flex items-center gap-3">
                <button
                    className="transition-colors duration-150 p-2 rounded-lg focus:outline-none cursor-pointer border-0 bg-transparent hover:opacity-80"
                    style={{ color: 'var(--text-muted)' }}
                    onClick={() => onToggleSidebar?.()}
                    aria-label="Toggle sidebar"
                >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                    </svg>
                </button>

                <div className="hidden sm:flex items-center gap-2">
                    <span
                        className="text-xs font-semibold px-2.5 py-1 rounded-full border"
                        style={{
                            backgroundColor: 'var(--accent-soft)',
                            color: 'var(--accent)',
                            borderColor: 'var(--border)',
                        }}
                    >
                        {userRole}
                    </span>
                </div>
            </div>

            {/* Right: Date & Time + Theme Toggle + Avatar Profile Menu */}
            <div className="ml-auto flex items-center gap-2.5 sm:gap-3">
                {/* Date/Time Indicator */}
                <div
                    className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg border"
                    style={{
                        backgroundColor: 'var(--surface-2)',
                        borderColor: 'var(--border)',
                    }}
                >
                    <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: 'var(--accent)' }} />
                    <span
                        className="text-[11px] font-medium font-mono tracking-tight select-none"
                        style={{ color: 'var(--text-muted)' }}
                        dangerouslySetInnerHTML={{ __html: current_date_time }}
                    />
                </div>

                {/* Theme Mode Switcher Button (System / Dark / Light) */}
                <button
                    type="button"
                    onClick={toggleTheme}
                    title={`Theme: ${mode} (${isDark ? 'Dark' : 'Light'}). Click to toggle.`}
                    aria-label="Toggle theme mode"
                    className="w-9 h-9 rounded-xl flex items-center justify-center border transition-all duration-200 cursor-pointer focus:outline-none hover:opacity-90 active:scale-95"
                    style={{
                        backgroundColor: 'var(--surface-2)',
                        borderColor: 'var(--border)',
                        color: 'var(--accent)',
                    }}
                >
                    {mode === 'system' ? (
                        <Monitor className="w-4 h-4" />
                    ) : isDark ? (
                        <Moon className="w-4 h-4" />
                    ) : (
                        <Sun className="w-4 h-4" />
                    )}
                </button>

                <AvatarMenu title={userName} initials={initials}>
                    <div className="px-4 py-2 border-b" style={{ borderBottomColor: 'var(--border)' }}>
                        <p className="text-sm font-bold m-0 leading-tight" style={{ color: 'var(--text)' }}>{userName}</p>
                        <p className="text-xs m-0 mt-0.5" style={{ color: 'var(--text-muted)' }}>{userRole}</p>
                    </div>

                    <MenuItem onClick={() => navigate('/profile')}>
                        <ListItemIcon><PersonOutline fontSize="small" style={{ color: 'var(--accent)' }} /></ListItemIcon>
                        <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>My Profile</span>
                    </MenuItem>

                    <MenuItem onClick={() => navigate('/service-definitions')}>
                        <ListItemIcon><ShieldOutlined fontSize="small" style={{ color: 'var(--accent)' }} /></ListItemIcon>
                        <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>Service Catalog</span>
                    </MenuItem>

                    <Divider style={{ borderColor: 'var(--border)' }} />

                    <MenuItem
                        onClick={handleLogout}
                        disabled={isLoggingOut}
                        style={{ color: 'var(--danger)' }}
                    >
                        <ListItemIcon>
                            {isLoggingOut ? (
                                <CircularProgress size={18} style={{ color: 'var(--danger)' }} />
                            ) : (
                                <Logout fontSize="small" style={{ color: 'var(--danger)' }} />
                            )}
                        </ListItemIcon>
                        <span className="text-sm font-semibold">
                            {isLoggingOut ? "Signing Out..." : "Sign Out"}
                        </span>
                    </MenuItem>
                </AvatarMenu>
            </div>
        </nav>
    );
}
